import api from '@/shared/utils/apiConfig';
import type { AvailableSlot } from '../types/scheduling';

export interface GetAvailableSlotsParams {
  staffId: string;
  clinicId: string;
  date: string;
  durationMinutes: number;
  stepMinutes?: number;
  excludeAppointmentId?: string;
}

export type AppointmentDateBlockReason =
  | 'date_in_past'
  | 'clinic_not_found'
  | 'clinic_closed'
  | 'staff_not_found'
  | 'schedule_not_configured'
  | 'staff_not_working'
  | 'staff_all_day_time_off'
  | 'outside_advance_window'
  | 'outside_lead_window';

export interface AppointmentDateCheckResult {
  ok: boolean;
  date: string;
  reason?: AppointmentDateBlockReason;
  message?: string;
}

export interface GetAppointmentDaysParams {
  staffId: string;
  clinicId: string;
  from: string;
  to: string;
}

export type BookingOptionStatus =
  | 'available'
  | 'working'
  | 'warn'
  | 'blocked'
  | 'not_working';

export interface BookingDoctorOption {
  id: string;
  firstName: string;
  lastName: string;
  specialization?: string;
  avatarUrl?: string | null;
  status?: BookingOptionStatus;
  message?: string;
}

export interface BookingChairOption {
  id: string;
  name: string;
  code?: string;
  status?: BookingOptionStatus;
  message?: string;
}

export interface BookingTimeOption {
  label: string;
  status: 'available' | 'warn' | 'blocked' | 'busy';
  message?: string;
  busyReason?: string;
}

export interface BookingDateOption {
  date: string;
  ok: boolean;
  reason?: AppointmentDateBlockReason;
  message?: string;
}

export interface BookingOptionsResult {
  policy: {
    overlap: { dentist: string; chair: string };
    requireChair: string;
    slotStepMinutes: number;
    buffers?: { beforeMinutes: number; afterMinutes: number };
    minLeadMinutes?: number;
    maxAdvanceDays?: number;
    schedule?: { clinicHours: string; staffSchedule: string };
  } | null;
  doctors: BookingDoctorOption[];
  chairs: BookingChairOption[];
  dates: BookingDateOption[];
  times: BookingTimeOption[];
}

export interface GetBookingOptionsParams {
  clinicId?: string;
  doctorId?: string;
  chairId?: string;
  appointmentTypeId?: string;
  date?: string;
  startTime?: string;
  durationMinutes?: number;
  from?: string;
  to?: string;
  excludeAppointmentId?: string;
}

export interface GetCalendarSlotsParams {
  clinicId: string;
  doctorIds: string[];
  from: string;
  to: string;
  durationMinutes?: number;
  excludeAppointmentId?: string;
}

export interface CalendarBusyTimeBlock {
  id: string;
  startTime: string;
  endTime: string;
  reason: string;
}

export interface CalendarDoctorDaySlots {
  doctorId: string;
  isWorking: boolean;
  workingWindows: Array<{ start: string; end: string }>;
  slots: BookingTimeOption[];
  busyTimes?: CalendarBusyTimeBlock[];
}

export interface CalendarDaySlots {
  date: string;
  doctors: CalendarDoctorDaySlots[];
}

export interface CalendarSlotsResult {
  days: CalendarDaySlots[];
}

export const getAvailableSlots = async (
  params: GetAvailableSlotsParams
): Promise<AvailableSlot[]> => {
  const response = await api.get('/scheduling/availability/slots', { params });
  return response.data.data ?? [];
};

export const getAppointmentDays = async (
  params: GetAppointmentDaysParams
): Promise<AppointmentDateCheckResult[]> => {
  const response = await api.get('/scheduling/availability/days', { params });
  return response.data.data ?? [];
};

export const getBookingOptions = async (
  params: GetBookingOptionsParams
): Promise<BookingOptionsResult> => {
  const response = await api.post('/scheduling/booking/options', params);
  return (
    response.data.data ?? {
      policy: null,
      doctors: [],
      chairs: [],
      dates: [],
      times: [],
    }
  );
};

export const getCalendarSlots = async (
  params: GetCalendarSlotsParams
): Promise<CalendarSlotsResult> => {
  const response = await api.post('/scheduling/booking/calendar-slots', params);
  return response.data.data ?? { days: [] };
};
